import React, { useState, useContext, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  CardActions,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { firebase_functions } from '../../../common/firebaseConfig'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import MetaApiAccountsDialog from '../../../components/dialogs/MetaApiAccountsDialog'
import ReconnectMetaApiAccountDialog from '../../../components/dialogs/ReconnectMetaApiAccountDialog'

interface Account {
  accountId: string
  login: string
  name: string
  server: string
  platform: string
  deploymentState?: 'DEPLOYED' | 'UNDEPLOYED' | 'DEPLOYING' | 'UNDEPLOYING'
}

interface MetaTraderAccountsTabProps {
  accounts: Account[]
}

function DeploymentStatusIndicator({ state }: { state?: string }) {
  if (state === 'DEPLOYING' || state === 'UNDEPLOYING') {
    return (
      <Tooltip title={state === 'DEPLOYING' ? 'Connecting...' : 'Disconnecting...'}>
        <CircularProgress size={12} sx={{ mr: 1 }} />
      </Tooltip>
    )
  }

  if (state === 'UNDEPLOYED') {
    return (
      <Tooltip title="Disconnected">
        <FiberManualRecordIcon sx={{ fontSize: 12, mr: 1, color: 'error.main' }} />
      </Tooltip>
    )
  }

  // DEPLOYED or undefined (legacy accounts were always deployed on creation)
  return (
    <Tooltip title="Connected">
      <FiberManualRecordIcon sx={{ fontSize: 12, mr: 1, color: 'success.main' }} />
    </Tooltip>
  )
}

const MetaTraderAccountsTab: React.FC<MetaTraderAccountsTabProps> = ({ accounts }) => {
  const theme = useTheme()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [undeployDialogOpen, setUndeployDialogOpen] = useState(false)
  const [accountToDelete, setAccountToDelete] = useState<Account | null>(null)
  const [accountToUndeploy, setAccountToUndeploy] = useState<Account | null>(null)
  const [accountToReset, setAccountToReset] = useState<Account | null>(null)
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const [removeMetaApiAccountFunc, removeMetaApiAccountLoading] = useHttpsCallable(
    firebase_functions,
    'removeMetaApiAccount'
  )
  const [deployFunc, deployLoading] = useHttpsCallable(firebase_functions, 'deployMetaApiAccount')
  const [undeployFunc, undeployLoading] = useHttpsCallable(
    firebase_functions,
    'undeployMetaApiAccount'
  )
  const [getStatusesFunc] = useHttpsCallable(firebase_functions, 'getMetaApiAccountStatuses')

  // Refresh account statuses from MetaAPI on mount
  useEffect(() => {
    if (accounts.length > 0) {
      getStatusesFunc({})
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isTransitioning = (account: Account) =>
    account.deploymentState === 'DEPLOYING' || account.deploymentState === 'UNDEPLOYING'

  const handleOpenDialog = () => {
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
  }

  // Delete handlers
  const handleDeleteClick = (account: Account) => {
    setAccountToDelete(account)
    setDeleteDialogOpen(true)
  }

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false)
    setAccountToDelete(null)
  }

  const handleDeleteAccount = async () => {
    if (!accountToDelete) return

    try {
      const response: any = await removeMetaApiAccountFunc({ accountId: accountToDelete.accountId })

      if (!response?.data?.status || response?.data?.status >= 400) {
        setSnackbarSettings({
          severity: 'error',
          message: response?.data?.message || 'Failed to remove MetaTrader account',
          autoHideDuration: 6000,
        })
        return
      }

      setSnackbarSettings({
        severity: 'success',
        message: 'MetaTrader account removed successfully!',
        autoHideDuration: 6000,
      })

      handleDeleteDialogClose()
    } catch (error) {
      setSnackbarSettings({
        severity: 'error',
        message: 'Failed to remove MetaTrader account',
        autoHideDuration: 6000,
      })
    }
  }

  // Undeploy handlers
  const handleUndeployClick = (account: Account) => {
    setAccountToUndeploy(account)
    setUndeployDialogOpen(true)
  }

  const handleUndeployDialogClose = () => {
    setUndeployDialogOpen(false)
    setAccountToUndeploy(null)
  }

  const handleUndeployAccount = async () => {
    if (!accountToUndeploy) return

    try {
      const response: any = await undeployFunc({ accountId: accountToUndeploy.accountId })

      if (!response?.data?.status || response?.data?.status >= 400) {
        setSnackbarSettings({
          severity: 'error',
          message: response?.data?.message || 'Failed to disconnect account',
          autoHideDuration: 6000,
        })
        return
      }

      setSnackbarSettings({
        severity: 'success',
        message: 'Account disconnected from broker',
        autoHideDuration: 6000,
      })
    } catch (error) {
      setSnackbarSettings({
        severity: 'error',
        message: 'Failed to disconnect account',
        autoHideDuration: 6000,
      })
    } finally {
      handleUndeployDialogClose()
    }
  }

  // Deploy handler
  const handleDeployAccount = async (account: Account) => {
    try {
      const response: any = await deployFunc({ accountId: account.accountId })

      if (!response?.data?.status || response?.data?.status >= 400) {
        setSnackbarSettings({
          severity: 'error',
          message: response?.data?.message || 'Failed to reconnect account',
          autoHideDuration: 6000,
        })
        return
      }

      setSnackbarSettings({
        severity: 'success',
        message: 'Account reconnected to broker!',
        autoHideDuration: 6000,
      })
    } catch (error) {
      setSnackbarSettings({
        severity: 'error',
        message: 'Failed to reconnect account',
        autoHideDuration: 6000,
      })
    }
  }

  return (
    <>
      <Card className="ph-no-capture" sx={{ backgroundColor: theme.palette.background.custom2 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <AccountBalanceIcon /> MetaTrader Accounts
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {accounts.length > 0 ? (
            <List>
              {accounts.map((account) => (
                <ListItem
                  key={account.accountId}
                  secondaryAction={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {account.deploymentState === 'UNDEPLOYED' ? (
                        <>
                          <Tooltip title="Reconnect to broker">
                            <IconButton
                              onClick={() => handleDeployAccount(account)}
                              disabled={deployLoading || isTransitioning(account)}
                              color="success"
                              size="small"
                            >
                              <PlayArrowIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset account (re-enter credentials)">
                            <IconButton
                              onClick={() => setAccountToReset(account)}
                              size="small"
                              color="info"
                            >
                              <RestartAltIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : !isTransitioning(account) ? (
                        <Tooltip title="Disconnect from broker">
                          <IconButton
                            onClick={() => handleUndeployClick(account)}
                            disabled={undeployLoading}
                            color="warning"
                            size="small"
                          >
                            <PowerSettingsNewIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="Remove MetaTrader account">
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDeleteClick(account)}
                          disabled={removeMetaApiAccountLoading || isTransitioning(account)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center">
                        <DeploymentStatusIndicator state={account.deploymentState} />
                        <Typography variant="subtitle1">{account.name}</Typography>
                      </Box>
                    }
                    secondary={
                      <Box component="span">
                        <Typography variant="body2" component="span">
                          Login: {account.login}
                        </Typography>
                        <Box mx={0.5} display={'inline-block'}>
                          •
                        </Box>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ color: 'text.secondary' }}
                        >
                          Server: {account.server}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AccountBalanceIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No MetaTrader Accounts
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Connect your MetaTrader account to enable automated trading with your alerts.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenDialog}
              >
                Add MetaTrader Account
              </Button>
            </Box>
          )}
        </CardContent>
        {accounts.length > 0 && (
          <CardActions>
            <Button
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              variant="outlined"
              color="primary"
            >
              Add Account
            </Button>
          </CardActions>
        )}
      </Card>

      <MetaApiAccountsDialog open={dialogOpen} onClose={handleCloseDialog} />

      {accountToReset && (
        <ReconnectMetaApiAccountDialog
          open={!!accountToReset}
          accounts={[{ ...accountToReset, platform: accountToReset.platform as 'mt4' | 'mt5' }]}
          onClose={() => setAccountToReset(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Remove MetaTrader Account</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to remove the MetaTrader account "{accountToDelete?.name}"?
            <br />
            <br />
            <strong>Account Details:</strong>
            <ul>
              <li>Nickname: {accountToDelete?.name}</li>
              <li>Server: {accountToDelete?.server}</li>
              <li>Login: {accountToDelete?.login}</li>
            </ul>
            <br />
            This action will:
            <ul>
              <li>Disconnect this MetaTrader account from your profile</li>
              <li>Disconnect any trading plans that use this account</li>
              <li>Stop any active trading operations for this account</li>
            </ul>
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary" variant="contained">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="outlined"
            disabled={removeMetaApiAccountLoading}
          >
            {removeMetaApiAccountLoading ? 'Removing...' : 'Remove Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Undeploy Confirmation Dialog */}
      <Dialog
        open={undeployDialogOpen}
        onClose={handleUndeployDialogClose}
        aria-labelledby="undeploy-dialog-title"
        aria-describedby="undeploy-dialog-description"
      >
        <DialogTitle id="undeploy-dialog-title">Disconnect MetaTrader Account</DialogTitle>
        <DialogContent>
          <DialogContentText id="undeploy-dialog-description">
            Are you sure you want to disconnect "{accountToUndeploy?.name}" from the broker?
            <br />
            <br />
            <strong>This will:</strong>
            <ul>
              <li>Disconnect the account from your broker</li>
              <li>Stop all automated trading for alerts using this account</li>
              <li>Prevent new trades and trade history from being received</li>
            </ul>
            <br />
            The account will remain on your profile. You can reconnect at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUndeployDialogClose} color="primary" variant="contained">
            Cancel
          </Button>
          <Button
            onClick={handleUndeployAccount}
            color="warning"
            variant="outlined"
            disabled={undeployLoading}
          >
            {undeployLoading ? 'Disconnecting...' : 'Disconnect Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default MetaTraderAccountsTab
